export type PermissionsRaw = string[];

export type RoleRaw = {
  id: string;
  name: string;
  description?: string;
  permissions: PermissionsRaw;
  created_at: string;
  updated_at: string;
  is_disabled: boolean;
};

export type CustomPermission = {
  rawPermission: string;
  identifier: string;
  action: PermissionsAction;
  ids: string[];
};

export type Role = Omit<RoleRaw, "permissions"> & {
  permissions: RolePermissions;
  customPermissions: CustomPermission[];
};

export type RoleRawForm = {
  name: string;
  description?: string;
  permissions: PermissionsRaw;
};

export type ParsedPermissions = {
  permissions: RolePermissions;
  customPermissions: CustomPermission[];
};

// https://developer.mozilla.org/en-US/docs/Web/API/Permissions
// 型名が重複するので、RolePermissionsとして定義
export type RolePermissions = {
  [K in
    | PermissionsKey
    | PermissionsWithIdKey
    | PermissionsWithIdsKey]: K extends PermissionsWithIdKey
    ? AllIdAction
    : K extends PermissionsWithIdsKey
      ? AllIdsAction
      : AllAction;
};

export const permissionsActions = [
  "read",
  "write",
  "delete",
] as const satisfies string[];
export type AllActionsType = (typeof permissionsActions)[number];
export type AllAction = {
  [K in AllActionsType]?: boolean;
};

export type AllIdAction = {
  [K in AllActionsType]: string[];
};

export type AllIdsAction = {
  [K in AllActionsType]: {
    versionId: string;
    sheetId: string;
  }[];
};

export const permissionsKeys = [
  "organizations",
  "members",
  "roles",
  "version_categories",
  "versions",
  "versions_meta",
  "optimize",
  "evaluate",
  "algorithm_versions",
  "view_masters",
  "connect_masters",
  "connect_masters_owned",
  "connects",
  "connect_jobs",
  "connect_job_histories",
  "frontend_addons",
  "frontend_addons_owned",
  "endpoint_addons",
  "organizations_notification_channel",
  "organizations_display_setting",
  "file_cell_large",
  "tad_sheets",
  "tad_sheets_meta",
] as const satisfies string[];

export const permissionsWithIdKeys = [
  "version_category",
  "versions_version_category",
  "sheet",
  "version",
  "tad_sheet",
] as const satisfies string[];

export const permissionsWithIdsKeys = [
  "version_sheet",
] as const satisfies string[];

export type PermissionsKey = (typeof permissionsKeys)[number];
export type PermissionsWithIdKey = (typeof permissionsWithIdKeys)[number];
export type PermissionsWithIdsKey = (typeof permissionsWithIdsKeys)[number];
export type PermissionsAction = (typeof permissionsActions)[number];

export type AllPermissionsKey =
  | PermissionsKey
  | PermissionsWithIdKey
  | PermissionsWithIdsKey;

export const orgSuperAdminRolePermissions = {
  organizations: {
    read: true,
    write: true,
  },
  organizations_notification_channel: {
    read: true,
    write: true,
  },
  organizations_display_setting: {
    write: true,
  },
  members: {
    read: true,
    write: true,
    delete: true,
  },
  roles: {
    read: true,
    write: true,
    delete: true,
  },
  version_categories: {
    read: true,
    write: true,
    delete: true,
  },
  algorithm_versions: {
    read: true,
  },
  view_masters: {
    read: true,
  },
  connect_masters: {
    read: true,
  },
  connect_masters_owned: {
    read: true,
    write: true,
    delete: true,
  },
  connects: {
    read: true,
    write: true,
    delete: true,
  },
  connect_jobs: {
    write: true,
  },
  connect_job_histories: {
    read: true,
  },
  frontend_addons: {
    read: true,
  },
  endpoint_addons: {
    read: true,
  },
  frontend_addons_owned: {
    read: true,
    write: true,
    delete: true,
  },
  optimize: {
    write: true,
  },
  evaluate: {
    write: true,
  },
  versions: {
    read: true,
    write: true,
    delete: true,
  },
  versions_meta: {
    write: true,
  },
  file_cell_large: {
    write: true,
  },
  tad_sheets: {
    read: true,
    write: true,
    delete: true,
  },
  tad_sheets_meta: {
    write: true,
  },
  version_category: { read: [], write: [], delete: [] },
  versions_version_category: {
    read: [],
    write: [],
    delete: [],
  },
  sheet: { read: [], write: [], delete: [] },
  version: { read: [], write: [], delete: [] },
  tad_sheet: { read: [], write: [], delete: [] },
  version_sheet: { read: [], write: [], delete: [] },
} as const satisfies RolePermissions;

export const orgAdminRolePermissions = {
  organizations: {
    read: true,
    write: true,
  },
  organizations_notification_channel: {
    read: false,
    write: false,
  },
  organizations_display_setting: {
    write: false,
  },
  members: {
    read: true,
    write: true,
    delete: true,
  },
  roles: {
    read: true,
    write: true,
    delete: true,
  },
  version_categories: {
    read: true,
    write: true,
    delete: true,
  },
  algorithm_versions: {
    read: true,
  },
  view_masters: {
    read: true,
  },
  connect_masters: {
    read: true,
  },
  connect_masters_owned: {
    read: false,
    write: false,
    delete: false,
  },
  connects: {
    read: true,
    write: true,
    delete: true,
  },
  connect_jobs: {
    write: true,
  },
  connect_job_histories: {
    read: true,
  },
  frontend_addons: {
    read: true,
  },
  endpoint_addons: {
    read: true,
  },
  frontend_addons_owned: {
    read: false,
    write: false,
    delete: false,
  },
  optimize: {
    write: true,
  },
  evaluate: {
    write: true,
  },
  versions: {
    read: true,
    write: true,
    delete: true,
  },
  versions_meta: {
    write: false,
  },
  file_cell_large: {
    write: false,
  },
  tad_sheets: {
    read: true,
    write: true,
    delete: false,
  },
  tad_sheets_meta: {
    write: false,
  },
  version_category: { read: [], write: [], delete: [] },
  versions_version_category: {
    read: [],
    write: [],
    delete: [],
  },
  sheet: { read: [], write: [], delete: [] },
  version: { read: [], write: [], delete: [] },
  tad_sheet: { read: [], write: [], delete: [] },
  version_sheet: { read: [], write: [], delete: [] },
} as const satisfies RolePermissions;

export const orgEditorRolePermissions = {
  organizations: {
    read: true,
    write: false,
  },
  organizations_notification_channel: {
    read: false,
    write: false,
  },
  organizations_display_setting: {
    write: false,
  },
  members: {
    read: true,
    write: false,
    delete: false,
  },
  roles: {
    read: true,
    write: false,
    delete: false,
  },
  version_categories: {
    read: true,
    write: false,
    delete: false,
  },
  algorithm_versions: {
    read: true,
  },
  view_masters: {
    read: true,
  },
  connect_masters: {
    read: true,
  },
  connect_masters_owned: {
    read: false,
    write: false,
    delete: false,
  },
  connects: {
    read: true,
    write: false,
    delete: false,
  },
  connect_jobs: {
    write: true,
  },
  connect_job_histories: {
    read: true,
  },
  frontend_addons: {
    read: true,
  },
  endpoint_addons: {
    read: true,
  },
  frontend_addons_owned: {
    read: false,
    write: false,
    delete: false,
  },
  optimize: {
    write: true,
  },
  evaluate: {
    write: true,
  },
  versions: {
    read: true,
    write: true,
    delete: true,
  },
  versions_meta: {
    write: false,
  },
  file_cell_large: {
    write: false,
  },
  tad_sheets: {
    read: true,
    write: true,
    delete: false,
  },
  tad_sheets_meta: {
    write: false,
  },
  version_category: { read: [], write: [], delete: [] },
  versions_version_category: {
    read: [],
    write: [],
    delete: [],
  },
  sheet: { read: [], write: [], delete: [] },
  version: { read: [], write: [], delete: [] },
  tad_sheet: { read: [], write: [], delete: [] },
  version_sheet: { read: [], write: [], delete: [] },
} as const satisfies RolePermissions;

export const orgViewerRolePermissions = {
  organizations: {
    read: true,
    write: false,
  },
  organizations_notification_channel: {
    read: false,
    write: false,
  },
  organizations_display_setting: {
    write: false,
  },
  members: {
    read: true,
    write: false,
    delete: false,
  },
  roles: {
    read: true,
    write: false,
    delete: false,
  },
  version_categories: {
    read: true,
    write: false,
    delete: false,
  },
  algorithm_versions: {
    read: true,
  },
  view_masters: {
    read: true,
  },
  connect_masters: {
    read: true,
  },
  connect_masters_owned: {
    read: false,
    write: false,
    delete: false,
  },
  connects: {
    read: true,
    write: false,
    delete: false,
  },
  connect_jobs: {
    write: false,
  },
  connect_job_histories: {
    read: true,
  },
  frontend_addons: {
    read: true,
  },
  endpoint_addons: {
    read: true,
  },
  frontend_addons_owned: {
    read: false,
    write: false,
    delete: false,
  },
  optimize: {
    write: false,
  },
  evaluate: {
    write: false,
  },
  versions: {
    read: true,
    write: false,
    delete: false,
  },
  versions_meta: {
    write: false,
  },
  file_cell_large: {
    write: false,
  },
  tad_sheets: {
    read: true,
    write: false,
    delete: false,
  },
  tad_sheets_meta: {
    write: false,
  },
  version_category: { read: [], write: [], delete: [] },
  versions_version_category: {
    read: [],
    write: [],
    delete: [],
  },
  sheet: { read: [], write: [], delete: [] },
  version: { read: [], write: [], delete: [] },
  tad_sheet: { read: [], write: [], delete: [] },
  version_sheet: { read: [], write: [], delete: [] },
} as const satisfies RolePermissions;

export const defaultOrgRoles = {
  "org:super_admin": orgSuperAdminRolePermissions,
  "org:admin": orgAdminRolePermissions,
  "org:editor": orgEditorRolePermissions,
  "org:viewer": orgViewerRolePermissions,
} as const;

export type OrgRoleId = keyof typeof defaultOrgRoles;
